import {
  AlertTriangle,
  CheckCircle2,
  ClipboardCheck,
  Droplets,
  Hexagon,
  Home,
  Shield,
  Sparkles,
  TrendingUp,
  User,
  Users,
} from "lucide-react";
import HeroStatusChip from "@/components/HeroStatusChip";
import WaterSafetyGuidanceChip from "@/components/WaterSafetyGuidanceChip";
import InterventionActionPlan from "@/components/InterventionActionPlan";
import { asInsightArray, registerToInsightLines, toInsightLines } from "@/utils/insightActionPlanDefaults";
import {
  buildExecutiveInsightCards,
  buildInsightsClosurePlan,
  buildInsightsHeroSummary,
  buildPriorityActionQueue,
  buildRootCauseSignals,
  computeBlockCohesion,
  computeDataCoverage,
  computeFieldValidationNeed,
  computeInsightConfidence,
  computeMetricConsistency,
  countPriorityGaps,
  deriveOwnerFromIssue,
  fmt,
  buildDataStatusChip,
  getFocusBlockNames,
  pct,
  round1,
} from "@/utils/waterSafetyDerived";
import { toTitleCase } from "../utils/toTitleCase";

const buildConfidenceGaugeStyle = (value) => ({
  background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(var(--inf-primary) 0 ${Math.min(value, 100)}%, rgba(37,99,235,.10) ${Math.min(value, 100)}% 100%)`,
});

const EXECUTIVE_ICONS = {
  classroom: { icon: CheckCircle2, iconTone: "green" },
  kitchen: { icon: Home, iconTone: "orange" },
  purification: { icon: AlertTriangle, iconTone: "red" },
  inclusion: { icon: User, iconTone: "purple" },
};

const SIGNAL_ICONS = {
  "water-quality": { icon: Droplets, iconTone: "red" },
  rwh: { icon: TrendingUp, iconTone: "orange" },
  inclusion: { icon: Users, iconTone: "purple" },
  hygiene: { icon: Shield, iconTone: "green" },
  testing: { icon: ClipboardCheck, iconTone: "cyan" },
};

const BriefMetricCard = ({ label, value, sub, icon: Icon, iconTone = "" }) => (
  <article className="inf-insights-brief-card">
    <small>
      {label}
      <span className={`inf-insights-card-icon ${iconTone}`}>
        <Icon size={18} />
      </span>
    </small>
    <strong>{value}</strong>
    <span>{sub}</span>
  </article>
);

const InsightCard = ({ icon: Icon, iconTone, severity, severityTone, title, value, description, progress, progressTone }) => (
  <article className="inf-insights-card">
    <div className="inf-insights-card-head">
      <div className={`inf-icon-box ${iconTone}`}>
        <Icon size={22} />
      </div>
      <span className={`inf-insights-severity ${severityTone}`}>{severity}</span>
    </div>
    <h3>{toTitleCase(title)}</h3>
    <div className="inf-insights-big-number">{value}</div>
    <p>{description}</p>
    <div className="inf-insights-progress-line">
      <i className={progressTone} style={{ width: `${Math.min(progress, 100)}%` }} />
    </div>
  </article>
);

const SignalItem = ({ icon: Icon, iconTone, title, description, score, scoreLabel }) => (
  <div className="inf-insights-signal-item">
    <div className={`inf-icon-box ${iconTone}`}>
      <Icon size={22} />
    </div>
    <div>
      <h4>{title}</h4>
      <p>{description}</p>
    </div>
    <div className="inf-insights-signal-score">
      <b>{score}</b>
      <span>{scoreLabel}</span>
    </div>
  </div>
);

const severityToneFromLabel = (label) => {
  const s = String(label ?? "").toLowerCase();
  if (s.includes("critical") || s.includes("high")) return "red";
  if (s.includes("watch") || s.includes("equity")) return "amber";
  if (s.includes("healthy") || s.includes("stable") || s.includes("on track")) return "";
  return "amber";
};

const progressFromValue = (value, fallback = 0) => {
  const parsed = parseFloat(String(value ?? "").replace("%", ""));
  return Number.isFinite(parsed) ? parsed : fallback;
};

const WaterSafetyInsightsTab = ({
  overview,
  blockData,
  bottomBlocks,
  guidance,
  guidanceProvider = "local",
  guidanceLoading = false,
  generatedAt = null,
}) => {
  if (!overview) {
    return (
      <div className="inf-empty-card">
        <Sparkles className="mx-auto mb-3 opacity-40" size={40} />
        <p>No insights data available for the selected scope.</p>
      </div>
    );
  }

  const aiConfidence = computeInsightConfidence(overview);
  const confidenceMetrics = {
    dataCoverage: Math.round(computeDataCoverage(overview)),
    metricConsistency: Math.round(computeMetricConsistency(overview)),
    blockVariation: Math.round(computeBlockCohesion(blockData, "water_safety_index")),
    fieldValidation: Math.round(computeFieldValidationNeed(overview)),
  };
  const priorityGaps = countPriorityGaps(overview);
  const heroSummary = guidance?.hero_summary ?? buildInsightsHeroSummary(overview);
  const focusBlockLabel = getFocusBlockNames(bottomBlocks, blockData, 3).join(", ") || "Low-score blocks";
  const confidenceSummary = guidance?.confidence_summary;

  const localExecutiveCards = buildExecutiveInsightCards(overview);
  const executiveSource = asInsightArray(guidance?.executive_cards, localExecutiveCards);
  const executiveCards = executiveSource.map((card) => {
    const meta = EXECUTIVE_ICONS[card.key] ?? { icon: Shield, iconTone: "" };
    const localMatch = localExecutiveCards.find((c) => c.key === card.key);
    const progress = localMatch?.progress ?? progressFromValue(card.value);
    return {
      ...card,
      icon: meta.icon,
      iconTone: meta.iconTone,
      severity: card.severity?.severity ?? card.severity,
      severityTone: card.severity?.severityTone ?? severityToneFromLabel(card.severity),
      progress,
      progressTone: localMatch?.progressTone ?? (progress >= 85 ? "green" : progress >= 70 ? "orange" : "red"),
    };
  });

  const localSignals = buildRootCauseSignals(overview);
  const signalSource = asInsightArray(guidance?.root_cause_signals, localSignals);
  const rootCauseSignals = signalSource.map((signal) => {
    const meta = SIGNAL_ICONS[signal.key] ?? { icon: Shield, iconTone: "" };
    return {
      ...signal,
      icon: meta.icon,
      iconTone: meta.iconTone,
      scoreLabel: signal.score_label ?? signal.scoreLabel,
    };
  });

  const actionQueue = asInsightArray(
    guidance?.action_queue,
    buildPriorityActionQueue(overview, blockData, bottomBlocks)
  ).map((row, index) => ({
    ...row,
    priority: row.priority ?? index + 1,
    owner: row.owner ?? deriveOwnerFromIssue(row.issue),
    statusTone: row.statusTone ?? severityToneFromLabel(row.status),
  }));

  const closurePlan = asInsightArray(
    guidance?.closure_plan,
    buildInsightsClosurePlan(overview, blockData, bottomBlocks)
  );

  const expectedOutcome =
    guidance?.expected_outcome ??
    `Lift WSI from ${round1(overview.water_safety_index).toFixed(1)} toward ${round1(
      Math.min(100, (overview.water_safety_index ?? 0) + priorityGaps * 2)
    ).toFixed(1)} by closing ${fmt(overview.purification_no)} purification and ${fmt(overview.rwh_non_func)} RWH gaps.`;

  const hygieneStrong = (overview.classroom_hygiene_coverage ?? 0) >= 90;

  const defaultRoot = toInsightLines(
    localSignals.map((signal) => `${signal.title}: ${signal.description ?? signal.body ?? ""}`)
  );
  const defaultRecs = toInsightLines(
    buildInsightsClosurePlan(overview, blockData, bottomBlocks).map(
      (step) => `${step.title}: ${step.description || ""}`
    )
  );
  const defaultActions = registerToInsightLines(
    buildPriorityActionQueue(overview, blockData, bottomBlocks)
  );
  const aiContent = guidance?.markdown || guidance?.ai_markdown || "";

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="inf-water-tab inf-insights-tab" data-testid="tab-insights-content" id="inf-insights-overview">
      <section className="inf-hero-card inf-hero-card-insights">
        <div className="inf-hero-layout">
          <div>
            <div className="inf-insights-main">
              <div
                className="inf-insights-gauge"
                style={buildConfidenceGaugeStyle(aiConfidence)}
                aria-label={`Insight confidence score ${aiConfidence} percent`}
              >
                <div>
                  <strong>{aiConfidence}%</strong>
                  <span>Insight Confidence</span>
                </div>
              </div>
              <div className="inf-hero-copy">
                <h2>AI Insights Command Centre</h2>
                <p>{heroSummary}</p>
                <div className="inf-status-row">
                  <HeroStatusChip
                    chipClass="inf-status-chip"
                    variant={hygieneStrong ? "green" : "amber"}
                    onClick={!hygieneStrong ? () => scrollTo("inf-insights-actions") : undefined}
                    ariaLabel={!hygieneStrong ? "Hygiene needs follow-up — view action plan" : undefined}
                  >
                    {hygieneStrong ? "Strong hygiene base" : "Hygiene needs follow-up"}
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="inf-status-chip"
                    variant="amber"
                    onClick={() => scrollTo("inf-insights-actions")}
                    ariaLabel={`${priorityGaps} priority gaps — view intervention actions`}
                  >
                    {priorityGaps} priority gaps
                  </HeroStatusChip>
                  <WaterSafetyGuidanceChip provider={guidanceProvider} loading={guidanceLoading} />
                  <HeroStatusChip chipClass="inf-status-chip" variant="blue">
                    {buildDataStatusChip(overview, generatedAt)}
                  </HeroStatusChip>
                </div>
              </div>
            </div>
          </div>

          <div className="inf-insights-brief-grid" aria-label="Decision summary KPIs">
            <BriefMetricCard
              label="Water Safety Index"
              value={round1(overview.water_safety_index ?? 0).toFixed(1)}
              sub="Composite water readiness score"
              icon={Droplets}
            />
            <BriefMetricCard
              label="Purification"
              value={pct(overview.purification_coverage)}
              sub={`${fmt(overview.purification_no)} schools without purification`}
              icon={Shield}
              iconTone="orange"
            />
            <BriefMetricCard
              label="RWH Coverage"
              value={pct(overview.rwh_coverage)}
              sub={`${fmt(overview.rwh_non_func)} non-functional of ${fmt(overview.rwh_yes)} RWH schools`}
              icon={Hexagon}
              iconTone="red"
            />
            <BriefMetricCard
              label="Inclusion Gap"
              value={pct(overview.inclusion_gap_pct)}
              sub={`${fmt(overview.special_educator_no)} schools without educator support`}
              icon={User}
              iconTone="red"
            />
          </div>
        </div>
      </section>

      <div className="inf-section-title">
        <div>
          <h2>Executive Briefing Cards</h2>
          <p>
            Metrics are converted into scannable decision cards so officers can identify what is good, what is
            risky, and what needs action.
          </p>
        </div>
        <button type="button" className="inf-section-action" onClick={() => scrollTo("inf-insights-actions")}>
          View action queue →
        </button>
      </div>

      <section className="inf-insights-grid" aria-label="Executive insight cards">
        {executiveCards.map((card) => (
          <InsightCard key={card.key} {...card} />
        ))}
      </section>

      <section className="inf-insights-two-column">
        <article className="inf-panel">
          <div className="inf-panel-head">
            <div>
              <h3>Root Cause Signals</h3>
              <p>Signals are grouped by operational cause derived from live facility metrics.</p>
            </div>
            <span className="inf-pill">Cause map</span>
          </div>
          <div className="inf-insights-signal-list">
            {rootCauseSignals.map((signal) => (
              <SignalItem key={signal.key} {...signal} />
            ))}
          </div>
        </article>

        <article className="inf-panel">
          <div className="inf-panel-head">
            <div>
              <h3>Confidence & Evidence</h3>
              <p>
                Confidence scores are derived from data coverage, metric consistency, block variation and field
                validation need.
              </p>
            </div>
            <span className="inf-pill">Evidence</span>
          </div>
          <div className="inf-insights-confidence-card">
            <div className="inf-insights-confidence-row">
              <span>Data coverage</span>
              <div className="inf-insights-track">
                <div className="inf-insights-bar green" style={{ width: `${confidenceMetrics.dataCoverage}%` }} />
              </div>
              <b>{confidenceMetrics.dataCoverage}%</b>
            </div>
            <div className="inf-insights-confidence-row">
              <span>Metric consistency</span>
              <div className="inf-insights-track">
                <div className="inf-insights-bar" style={{ width: `${confidenceMetrics.metricConsistency}%` }} />
              </div>
              <b>{confidenceMetrics.metricConsistency}%</b>
            </div>
            <div className="inf-insights-confidence-row">
              <span>Block cohesion</span>
              <div className="inf-insights-track">
                <div className="inf-insights-bar orange" style={{ width: `${confidenceMetrics.blockVariation}%` }} />
              </div>
              <b>{confidenceMetrics.blockVariation}%</b>
            </div>
            <div className="inf-insights-confidence-row">
              <span>Field validation need</span>
              <div className="inf-insights-track">
                <div className="inf-insights-bar orange" style={{ width: `${confidenceMetrics.fieldValidation}%` }} />
              </div>
              <b>{confidenceMetrics.fieldValidation}%</b>
            </div>
            <div className="inf-insights-evidence-box">
              <b>Evidence summary</b>
              <p>
                {confidenceSummary ||
                  `Based on ${fmt(overview.total_schools)} schools: tap ${pct(overview.tap_water_coverage)}, purification ${pct(overview.purification_coverage)}, testing ${pct(overview.water_testing_coverage)}, RWH ${pct(overview.rwh_coverage)}, hygiene ${pct(overview.classroom_hygiene_coverage)}, inclusion gap ${pct(overview.inclusion_gap_pct)}. Lowest WSI blocks: ${focusBlockLabel}.`}
              </p>
            </div>
          </div>
        </article>
      </section>

      <section className="inf-action-plan">
        <InterventionActionPlan
          aiContent={aiContent}
          defaultRoot={defaultRoot}
          defaultRecs={defaultRecs}
          defaultActions={defaultActions}
        />
      </section>

      <div className="inf-section-title" id="inf-insights-actions">
        <div>
          <h2>Recommended Actions and 30-Day Plan</h2>
          <p>Action items are ranked by live gap counts with derived targets and urgency.</p>
        </div>
        <button type="button" className="inf-section-action" onClick={() => scrollTo("inf-insights-overview")}>
          Back to top ↑
        </button>
      </div>

      <section className="inf-insights-action-layout">
        <article className="inf-panel inf-table-card">
          <div className="inf-table-head">
            <div>
              <h3>Priority Action Queue</h3>
              <p>Ranked by gap size from current infrastructure data.</p>
            </div>
            <span className="inf-pill">{actionQueue.length} actions</span>
          </div>
          <div className="inf-table-wrap">
            <table className="inf-data-table" aria-label="Water safety priority action queue">
              <thead>
                <tr>
                  <th>Priority</th>
                  <th>Issue</th>
                  <th>Focus Blocks</th>
                  <th>Owner</th>
                  <th>Target</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {actionQueue.map((row) => (
                  <tr key={row.priority}>
                    <td>
                      <span className="inf-insights-priority-number">{row.priority}</span>
                    </td>
                    <td>{row.issue}</td>
                    <td>{row.blocks}</td>
                    <td>{row.owner}</td>
                    <td>{row.target}</td>
                    <td>
                      <span className={`inf-tag ${row.statusTone}`}>{row.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </article>

        <article className="inf-panel">
          <div className="inf-panel-head">
            <div>
              <h3>30-Day Closure Plan</h3>
              <p>Phased plan with counts derived from current gaps.</p>
            </div>
            <span className="inf-pill">Sprint</span>
          </div>
          <div className="inf-insights-plan">
            {closurePlan.map((step) => (
              <div className="inf-insights-plan-step" key={step.step}>
                <span className="inf-insights-step-num">{step.step}</span>
                <div>
                  <h4>{step.title}</h4>
                  <p>{step.description}</p>
                </div>
              </div>
            ))}
            <div className="inf-insights-plan-footer">
              <b>Expected outcome</b>
              <span>{expectedOutcome}</span>
            </div>
          </div>
        </article>
      </section>
    </div>
  );
};

export default WaterSafetyInsightsTab;
